/**
 * Funções HTTP para autenticação contra a API. // ux-neutrality:allow doc-comment
 *
 * Encapsula o contrato de POST /api/auth/jwt/login (form-urlencoded), // ux-neutrality:allow doc-comment
 * POST /api/auth/jwt/logout e GET /api/users/me. Mapeia erros do fastapi-users // ux-neutrality:allow doc-comment
 * (HTTP 400 LOGIN_BAD_CREDENTIALS) em uma `AuthError` discriminada que a UI // ux-neutrality:allow doc-comment
 * traduz para chave de i18n. // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

export interface LoginResponse {
  access_token: string; // ux-neutrality:allow auth-token (contrato fastapi-users, HTTP/RFC 7519)
  token_type: string; // ux-neutrality:allow auth-token (contrato fastapi-users, HTTP/RFC 7519)
}

/**
 * E52.F1: `certificador` só existe no escritório-plataforma — é quem assina o
 * laudo certificado (o titular NÃO herda esse poder). Omiti-lo aqui fazia a
 * tela de usuários tratar o papel como desconhecido e rebaixá-lo por engano.
 */
export type UsuarioRole = 'owner' | 'member' | 'revisor' | 'certificador';

/** E50.F2: papéis do ambiente banco (fundação E50.F1 — ck_users_role). */
export type BankRole = 'bank_manager' | 'bank_user';

/** União de papéis aceitos pelo /api/perfil pós-E50.F1. */
export type PerfilRole = UsuarioRole | BankRole;

/**
 * E50.F2: ambiente do usuário autenticado (ADR-0059). Decide qual área da SPA
 * é a casa dele: `interno` = área do perito (escritórios legados e plataforma);
 * `escritorio` = escritório-cliente vinculado a banco (portal); `banco` =
 * usuário corporativo do banco (portal). Payloads de /api/perfil anteriores à
 * E50.F3 não trazem o campo — o default é `interno`.
 */
export type Ambiente = 'interno' | 'escritorio' | 'banco';

export interface CurrentUser {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  escritorio_id: string | null;
  role: UsuarioRole;
}

export interface SignupResponse {
  id: string;
  email: string;
  escritorio_id: string;
}

export type AuthErrorReason = 'invalid_credentials' | 'too_many_attempts' | 'network' | 'unknown';

// E166.F2.T02: a api responde 429 `MUITAS_TENTATIVAS` (com `Retry-After`) depois do limite de
// tentativas nas rotas sem login — entrar, recuperar a senha, redefinir e cadastrar.
function _muitasTentativas(err: AxiosError): boolean {
  return err.response?.status === 429;
}

export class AuthError extends Error {
  public readonly reason: AuthErrorReason;

  constructor(reason: AuthErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'AuthError';
  }
}

export type SignupErrorReason =
  | 'email_already_registered'
  | 'validation'
  | 'closed'
  | 'too_many_attempts'
  | 'network'
  | 'unknown';

export class SignupError extends Error {
  public readonly reason: SignupErrorReason;

  constructor(reason: SignupErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'SignupError';
  }
}

function _classify(err: unknown): AuthError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;
    if (axiosErr.response?.status === 400) {
      return new AuthError('invalid_credentials');
    }
    if (_muitasTentativas(axiosErr)) {
      return new AuthError('too_many_attempts');
    }
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
      return new AuthError('network');
    }
  }
  return new AuthError('unknown');
}

export async function login(
  client: AxiosInstance,
  email: string,
  password: string,
): Promise<LoginResponse> {
  try {
    const body = new URLSearchParams({ username: email, password });
    const { data } = await client.post<LoginResponse>('/auth/jwt/login', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

function _classifySignup(err: unknown): SignupError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;
    if (axiosErr.response?.status === 409) {
      return new SignupError('email_already_registered');
    }
    if (axiosErr.response?.status === 422) {
      return new SignupError('validation');
    }
    if (axiosErr.response?.status === 403) {
      return new SignupError('closed');
    }
    if (_muitasTentativas(axiosErr)) {
      return new SignupError('too_many_attempts');
    }
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
      return new SignupError('network');
    }
  }
  return new SignupError('unknown');
}

export async function signup(
  client: AxiosInstance,
  email: string,
  password: string,
  nomeEscritorio: string,
): Promise<SignupResponse> {
  try {
    const { data } = await client.post<SignupResponse>('/auth/signup', {
      email,
      password,
      nome_escritorio: nomeEscritorio,
    });
    return data;
  } catch (err) {
    throw _classifySignup(err);
  }
}

/**
 * Se o cadastro público está aberto (E166.F2.T03). Fechado em produção e homologação: as contas
 * nascem pelo administrador do escritório, pelo gestor do banco ou pelo console. Na dúvida (falha
 * de rede), fechado — a tela não oferece um caminho que a api recusa.
 */
export async function cadastroPublicoAberto(client: AxiosInstance): Promise<boolean> {
  try {
    const { data } = await client.get<{ aberto: boolean }>('/auth/cadastro');
    return data.aberto === true;
  } catch {
    return false;
  }
}

export type ResetPasswordErrorReason =
  | 'invalid_token' // ux-neutrality:allow auth-token (estado do contrato de reset, RFC 7519)
  | 'weak_password'
  | 'too_many_attempts'
  | 'network'
  | 'unknown';

export class ResetPasswordError extends Error {
  public readonly reason: ResetPasswordErrorReason;

  constructor(reason: ResetPasswordErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'ResetPasswordError';
  }
}

/**
 * Solicita o e-mail de redefinição de senha (E35.F2). // ux-neutrality:allow doc-comment
 *
 * POST /api/auth/forgot-password responde sempre 202 (neutro — não revela se a // ux-neutrality:allow doc-comment
 * conta existe). Só falha em erro de rede; o chamador exibe a mesma confirmação // ux-neutrality:allow doc-comment
 * neutra independentemente de a conta existir. // ux-neutrality:allow doc-comment
 */
export async function forgotPassword(client: AxiosInstance, email: string): Promise<void> {
  try {
    await client.post('/auth/forgot-password', { email });
  } catch (err) {
    if (axios.isAxiosError(err) && _muitasTentativas(err)) {
      throw new ResetPasswordError('too_many_attempts');
    }
    if (axios.isAxiosError(err) && (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED')) {
      throw new ResetPasswordError('network');
    }
    throw new ResetPasswordError('unknown');
  }
}

export type CodigoErrorReason = 'invalid_code' | 'too_many_attempts' | 'network' | 'unknown';

export class CodigoError extends Error {
  public readonly reason: CodigoErrorReason;

  constructor(reason: CodigoErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'CodigoError';
  }
}

function _classifyCodigo(err: unknown): CodigoError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    // 400 `CODIGO_INVALIDO` (errado, vencido, já usado, e-mail sem conta) e 422 (fora do formato).
    if (status === 400 || status === 422) return new CodigoError('invalid_code');
    if (_muitasTentativas(err)) return new CodigoError('too_many_attempts');
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
      return new CodigoError('network');
    }
  }
  return new CodigoError('unknown');
}

export interface PedidoDeCodigo {
  validade_em_minutos: number;
  reenviar_em_segundos: number;
}

/**
 * Pede o código de acesso para o e-mail (ADR-0149). // ux-neutrality:allow doc-comment
 *
 * POST /api/auth/codigo responde 202 igual com ou sem conta: a tela mostra a mesma confirmação. // ux-neutrality:allow doc-comment
 */
export async function pedirCodigo(client: AxiosInstance, email: string): Promise<PedidoDeCodigo> {
  try {
    const { data } = await client.post<PedidoDeCodigo>('/auth/codigo', { email });
    return data;
  } catch (err) {
    throw _classifyCodigo(err);
  }
}

/**
 * Entra com o código recebido no e-mail (ADR-0149): o mesmo corpo do login com senha. // ux-neutrality:allow doc-comment
 */
export async function entrarComCodigo(
  client: AxiosInstance,
  email: string,
  codigo: string,
): Promise<LoginResponse> {
  try {
    const { data } = await client.post<LoginResponse>('/auth/codigo/entrar', { email, codigo });
    return data;
  } catch (err) {
    throw _classifyCodigo(err);
  }
}

function _classifyReset(err: unknown): ResetPasswordError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<{ detail?: unknown }>;
    if (axiosErr.response?.status === 400) {
      const detail = axiosErr.response.data?.detail;
      // fastapi-users devolve { code: 'RESET_PASSWORD_INVALID_PASSWORD' } p/ senha
      // fraca e a string 'RESET_PASSWORD_BAD_TOKEN' p/ link inválido/expirado.
      if (
        typeof detail === 'object' &&
        detail !== null &&
        (detail as { code?: string }).code === 'RESET_PASSWORD_INVALID_PASSWORD'
      ) {
        return new ResetPasswordError('weak_password');
      }
      return new ResetPasswordError('invalid_token');
    }
    if (_muitasTentativas(axiosErr)) {
      return new ResetPasswordError('too_many_attempts');
    }
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
      return new ResetPasswordError('network');
    }
  }
  return new ResetPasswordError('unknown');
}

/**
 * Redefine a senha a partir do link recebido por e-mail (E35.F2). // ux-neutrality:allow doc-comment
 *
 * POST /api/auth/reset-password troca a senha. 400 = link inválido/expirado ou // ux-neutrality:allow doc-comment
 * senha fraca (discriminados em `ResetPasswordError.reason`). // ux-neutrality:allow doc-comment
 */
export async function resetPassword(
  client: AxiosInstance,
  resetToken: string, // ux-neutrality:allow auth-token (contrato fastapi-users, RFC 7519)
  password: string,
): Promise<void> {
  try {
    await client.post('/auth/reset-password', { token: resetToken, password }); // ux-neutrality:allow auth-token
  } catch (err) {
    throw _classifyReset(err);
  }
}

// Os parâmetros JWT abaixo são convenção HTTP (RFC 7519) e nunca aparecem em
// strings de UI. Bypass por linha (forma documentada). ux-neutrality:allow auth-token
export async function logout(
  client: AxiosInstance,
  token: string, // ux-neutrality:allow auth-token (JWT, RFC 7519)
): Promise<void> {
  await client.post(
    '/auth/jwt/logout',
    undefined,
    { headers: { Authorization: `Bearer ${token}` } }, // ux-neutrality:allow auth-token
  );
}

export async function fetchMe(
  client: AxiosInstance,
  token: string, // ux-neutrality:allow auth-token (JWT, RFC 7519)
): Promise<CurrentUser> {
  const { data } = await client.get<CurrentUser>(
    '/users/me',
    { headers: { Authorization: `Bearer ${token}` } }, // ux-neutrality:allow auth-token
  );
  return data;
}
